import React, { useLayoutEffect, useEffect, useState, useCallback, useRef } from "react";
import { Button, Col, Form, Row, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { getBookingData } from "../actions/billActions";
import { adminGetAllUsers } from "../actions/userActions";
import axios from "axios";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";
import useIsMobile from "../utils/useIsMobile";
import NoKeyboardInput from "../components/NoKeyboardInput";
import FormField from "../components/FormField";

const format_rupees = (val) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(val);

const format_date = (val) => {
  var customParseFormat = require('dayjs/plugin/customParseFormat');
  dayjs.extend(customParseFormat);
  let format = ["DD/MM/YYYY hh:mm:ss a", "D/M/YYYY H:mm:ss a", "YYYY-MM-DDTHH:mm:ss.SSSZ"];
  return dayjs(val, format).format("DD-MM-YYYY hh:mm a");
};

const Bookings = () => {
  const history = useHistory();
  const userInfo = useSelector(state => state.userLogin.userInfo);
  const [startDate, setStartDate] = useState(new Date(dayjs().subtract(1, 'day')));
  const [endDate, setEndDate] = useState(new Date(dayjs().add(1, 'month')));
  const [patientName, setPatientName] = useState("");
  const dispatch = useDispatch();
  const invoices = useSelector((state) => state.billInfo.bookData);
  const loading = useSelector((state) => state.billInfo.bookLoading);
  const { SearchBar } = Search;
  const isMobile = useIsMobile();
  const isAdmin = userInfo && userInfo.role === "admin";
  const { users: adminUsers } = useSelector(state => state.adminUsers || { users: [] });
  const [selectedUser, setSelectedUser] = useState("");
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const datePickerRef = useRef(null);

  const onDateChange = useCallback((dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    if (end) {
      datePickerRef.current?.setOpen(false);
    }
  }, []);

  const handleSearch = useCallback(() => {
    let filters = {
      start_date: startDate,
      end_date: endDate,
      patient_name: patientName
    };
    if (isAdmin && selectedUser) filters.userId = selectedUser;
    dispatch(getBookingData(filters));
  }, [dispatch, startDate, endDate, patientName, isAdmin, selectedUser]);

  useLayoutEffect(() => {
    handleSearch();
  }, [handleSearch]);

  useEffect(() => {
    if (isAdmin) dispatch(adminGetAllUsers());
  }, [isAdmin, dispatch]);

  const editInvoice = useCallback((invoiceNo) => {
    history.push(`/booking?id=${invoiceNo}`);
  }, [history]);

  const deleteBooking = useCallback(async (invoiceNo) => {
    const config = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userInfo.token}`,
      },
    };
    try {
      const res = await axios.delete(`/api/v1/booking/delete/${invoiceNo}`, config);
      if (res.status === 200) {
        alert("Deleted successfully");
        handleSearch();
      }
    } catch {
      alert("Unable to delete, Please try after sometime");
    }
  }, [userInfo.token, handleSearch]);

  const columns = [
    ...(isAdmin ? [{
      dataField: "user_name",
      text: "User / Lab",
      sort: true,
      formatter: (cellContent, row) => (
        <div>
          <div className="font-weight-bold">{row.user_name || 'Admin'}</div>
          <div className="small text-muted">{row.com_name || 'N/A'}</div>
        </div>
      )
    }] : []),
    {
      dataField: "booking_date",
      text: "Booking Date",
      sort: true,
      formatter: (cell) => {
        const dateStr = format_date(cell);
        const [datePart, ...timeParts] = dateStr.split(" ");
        return (
          <div>
            <div>{datePart}</div>
            <div className="text-muted small">{timeParts.join(" ")}</div>
          </div>
        );
      }
    },
    {
      dataField: "patient_name",
      text: "Patient Name",
      sort: true
    },
    {
      dataField: "patient_gender",
      text: "Gender",
      sort: true
    },
    {
      dataField: "patient_mobile",
      text: "Mobile No.",
      sort: true
    },
    {
      dataField: "net_amount",
      text: "Net Amount",
      headerAlign: 'right',
      sort: true,
      style: { textAlign: 'right' },
      formatter: cell => format_rupees(cell)
    },
    {
      dataField: "actions",
      text: "",
      isDummyField: true,
      headerStyle: { width: '60px', verticalAlign: 'middle' },
      classes: 'table-action-cell',
      formatter: (cellContent, row) => (
        <div className="d-flex align-items-center justify-content-center" style={{ height: '100%' }}>
          <Button
            variant="link"
            className="py-0 px-1 text-danger"
            title="Delete Booking"
            onClick={e => {
              e.stopPropagation();
              if (window.confirm(`Delete booking for ${row.patient_name}?`)) {
                deleteBooking(row.id);
              }
            }}
            style={{ lineHeight: 1, display: 'flex', alignItems: 'center' }}
          >
            <i className="fa fa-trash" style={{ fontSize: '18px' }}></i>
          </Button>
        </div>
      )
    }
  ];

  const mobColumns = [
    {
      dataField: "booking_date",
      text: "Booking Date",
      sort: true,
      formatter: (cell) => {
        const dateStr = format_date(cell);
        const [datePart, ...timeParts] = dateStr.split(" ");
        return (
          <div style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
            <div>{datePart}</div>
            <div className="text-muted small">{timeParts.join(" ")}</div>
          </div>
        );
      }
    },
    {
      dataField: "patient_name",
      text: "Patient Name",
      sort: true
    }
  ];

  const columnsToUse = isMobile ? mobColumns : columns;

  return (
    <div className="bookings-screen pb-5">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-end mb-4">
        <div className={isMobile ? "mb-3" : ""}>
          <h3 className="font-weight-bold grey-900 mb-1" style={{ fontSize: 'clamp(1rem, 4vw, 1.4rem)', letterSpacing: '-0.5px' }}>
            Bookings History
          </h3>
          <p className="text-muted mb-0 small uppercase font-weight-bold letter-spacing-1">
            <i className="fa fa-history mr-2 text-primary"></i> Track and manage all patient bookings
          </p>
        </div>
        <Button
          variant="success"
          onClick={() => history.push("/booking")}
          className={`btn-action-premium btn-generate-trf py-2 px-4 ${isMobile ? 'w-100' : ''}`}
          style={{ minWidth: isMobile ? '100%' : '200px', borderRadius: '10px' }}
        >
          <i className="fa fa-plus-circle mr-2"></i> Create New Booking
        </Button>
      </div>

      <div className="billing-form-card custom-card-boxshadow mb-5 p-4" style={{ borderRadius: '20px', border: '1px solid rgba(255,255,255,0.8)', background: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(10px)' }}>
        <Row className="align-items-end">
          {isAdmin && (
            <Col xs={12} md={3}>
              <FormField label="Filter by User" containerClassName="mb-3 mb-md-0">
                <Form.Control
                  as="select"
                  className="premium-input border-0 shadow-sm"
                  value={selectedUser}
                  onChange={e => setSelectedUser(e.target.value)}
                >
                  <option value="">All Users</option>
                  {adminUsers && adminUsers.filter(u => u.role !== 'admin').map((u) => (
                    <option key={u.id} value={u.user_id}>{u.user_name}</option>
                  ))}
                </Form.Control>
              </FormField>
            </Col>
          )}
          <Col xs={12} md={isAdmin ? 3 : 4}>
            <FormField label="Select Date Range" containerClassName="mb-3 mb-md-0">
              <DatePicker
                ref={datePickerRef}
                placeholderText="Choose duration..."
                selected={startDate}
                onChange={onDateChange}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                dateFormat="dd MMM yyyy"
                portalId="root-portal"
                popperPlacement="bottom-start"
                withPortal={isMobile}
                customInput={
                  <NoKeyboardInput className="form-control bg-light border-0" style={{ cursor: 'pointer' }} />
                }
              />
            </FormField>
          </Col>
          <Col xs={12} md={isAdmin ? 3 : 5}>
            <FormField label="Search Patient" containerClassName="mb-3 mb-md-0">
              <div className="d-flex align-items-center position-relative">
                <i className="fa fa-search position-absolute text-muted" style={{ left: '14px', zIndex: 5 }}></i>
                <Form.Control
                  type="text"
                  className="premium-input border-0 shadow-sm"
                  style={{ paddingLeft: '40px' }}
                  placeholder="Enter name or mobile..."
                  value={patientName}
                  onChange={e => setPatientName(e.target.value)}
                />
              </div>
            </FormField>
          </Col>
          <Col xs={12} md={3} className="d-grid">
            <Button onClick={handleSearch} variant="primary" className="btn-action-premium shadow-lg w-100" style={{ height: '40px', background: '#0d6efd' }}>
              Search
            </Button>
          </Col>
        </Row>
      </div>

      <div className="billing-form-card custom-card-boxshadow p-0 overflow-hidden">
        {!loading ? (
          <ToolkitProvider
            keyField="id"
            data={invoices || []}
            columns={columnsToUse}
            search
          >
            {props => (
              <div className="premium-table-container">
                <div className="premium-table-header d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                  {/* <span className={`small font-weight-bold text-muted text-uppercase letter-spacing-1 ${isMobile ? "mb-2" : ""}`}>Bookings Results ({invoices.length})</span> */}
                  <div className="search-bar-container w-100 w-md-auto" style={{ maxWidth: isMobile ? '100%' : '300px' }}>
                    <SearchBar {...props.searchProps} placeholder="Search bookings..." />
                  </div>
                </div>

                {/* Mobile View: Cards */}
                <div className="d-block d-md-none p-3 bg-light">
                  {invoices && invoices.length > 0 ? (
                    invoices.map((row, idx) => (
                      <div key={idx} className="billing-form-card mb-3 p-3 border-0 shadow-sm" style={{ background: '#fff', cursor: 'pointer' }} onClick={() => { setSelectedRow(row); setShowActionModal(true); }}>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="font-weight-bold text-primary" style={{ fontSize: '0.95rem' }}>{format_date(row.booking_date)}</div>
                        </div>
                        <h5 className="font-weight-bold mb-1 text-dark" style={{ fontSize: '1.25rem' }}>{row.patient_name}</h5>
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          <div className="text-muted" style={{ fontSize: '0.95rem' }}>
                            <i className="fa fa-user-circle-o mr-1"></i> {row.patient_gender} <span className="mx-1">•</span> <i className="fa fa-phone mr-1"></i> <a href={`tel:${row.patient_mobile}`} className="text-decoration-none text-link font-weight-bold" onClick={(e) => e.stopPropagation()}>{row.patient_mobile}</a>
                          </div>
                          <div className="font-weight-bold text-success" style={{ fontSize: '1.2rem' }}>
                            {format_rupees(row.net_amount)}
                          </div>
                        </div>
                        {row.user_name && (
                          <div className="mt-2 pt-2 border-top text-muted italic" style={{ fontSize: '0.9rem' }}>
                            Assigned to: <span className="font-weight-bold">{row.user_name}</span>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="p-5 text-center text-muted italic bg-white rounded-lg">
                      <i className="fa fa-info-circle mr-2"></i> No bookings found.
                    </div>
                  )}
                </div>

                {/* Desktop View: Table */}
                <div className="d-none d-md-block">
                  <BootstrapTable
                    {...props.baseProps}
                    bordered={false}
                    hover
                    rowStyle={{ cursor: 'pointer' }}
                    rowEvents={{
                      onClick: (e, row) => {
                        editInvoice(row.id);
                      }
                    }}
                    noDataIndication={() => (
                      <div className="p-5 text-center text-muted italic">
                        <i className="fa fa-info-circle mr-2"></i> No bookings found for the selected criteria.
                      </div>
                    )}
                    pagination={paginationFactory({
                      showTotal: true,
                      sizePerPage: 10,
                      hideSizePerPage: true,
                      className: "premium-pagination"
                    })}
                    wrapperClasses="table-responsive"
                    tableStyle={{ tableLayout: 'auto', whiteSpace: 'nowrap' }}
                  />
                </div>
              </div>
            )}
          </ToolkitProvider>
        ) : (
          <div className="p-5 text-center">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="mt-3 text-muted">Loading bookings...</p>
          </div>
        )}
      </div>

      <Modal show={showActionModal} onHide={() => setShowActionModal(false)} centered size="sm" contentClassName={isMobile ? "border-0 shadow-lg" : ""} style={isMobile ? { borderRadius: '15px' } : {}}>
        <Modal.Header closeButton className={isMobile ? "border-0 pb-0" : "pb-0"}>
          <Modal.Title className="h6 font-weight-bold">Booking Actions</Modal.Title>
        </Modal.Header>
        <Modal.Body className={isMobile ? "p-4 text-center" : "p-3 text-center"}>
          <div className="d-flex justify-content-center" style={{ gap: isMobile ? '15px' : '10px' }}>
            <Button
              variant="warning"
              className={isMobile ? "px-4 py-2 font-weight-bold shadow-sm flex-fill" : ""}
              style={isMobile ? { borderRadius: '8px' } : { borderRadius: '8px', lineHeight: '0.5rem' }}
              onClick={() => { setShowActionModal(false); editInvoice(selectedRow?.id); }}
            >
              <i className={`fa fa-pencil-square mr-2`}></i> Edit
            </Button>
            <Button
              variant="danger"
              className={isMobile ? "px-4 py-2 font-weight-bold shadow-sm flex-fill" : ""}
              style={isMobile ? { borderRadius: '8px' } : { borderRadius: '8px', lineHeight: '0.5rem' }}
              onClick={() => { setShowActionModal(false); deleteBooking(selectedRow?.id); }}
            >
              <i className={`fa fa-trash mr-2`}></i> Delete
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div >
  );
};

export default Bookings;
